<script setup lang="ts">
import AppTabs from "@/components/shared/AppTabs.vue";
import CatMascot from "@/components/doctor-performance/cats/CatMascot.vue";
import CatSpeechBubble from "@/components/doctor-performance/cats/CatSpeechBubble.vue";
import SmartAccountingDrilldownDialog from "@/components/smart-accounting/SmartAccountingDrilldownDialog.vue";
import SmartAccountingSettingsDialog from "@/components/smart-accounting/SmartAccountingSettingsDialog.vue";
import { METRIC_LOOK, useAccountingCatPhrases } from "@/components/smart-accounting/smartAccountingUi";
import { ACCOUNTING_PERIOD_PRESETS, createSmartAccounting, provideSmartAccounting, useSmartAccountingFormat } from "@/composables/useSmartAccounting";
import { CASHIER_METRICS } from "@/type/smartAccounting";
import type { TabItem } from "@/type/tabs";

/**
 * Smart accounting — what each cashier, till and branch did with the money, per day, calculated
 * from the sales, returns, payments, expenses and hand-overs instead of a hand-kept sheet.
 * Thirteen tabs share one filter bar and one loaded report (`useSmartAccounting`). Until
 * `cashier_daily_report` is deployed the page runs on sample data and says so with a chip; see
 * docs/README.smart-accounting-backend-prompt.md.
 *
 * Managers only. Nobody is put on a "worst" list: shortages and discounts only appear as
 * suggestions to look at together.
 */

// Static literal on purpose (definePage is hoisted); mirrors PAGE_KEYS.ACCOUNTING_SMART_DASHBOARD.
definePage({ meta: { permissionKey: "page.accounting.smart_dashboard" } });

const { t, locale } = useI18n({ useScope: "global" });
const { phrase } = useAccountingCatPhrases();

const activeTab = ref("");

const ctx = createSmartAccounting({ openTab: (tab) => { activeTab.value = tab; } });
provideSmartAccounting(ctx);
const { formatCompactMoney } = useSmartAccountingFormat();

const tab = (value: string, icon: string, loader: TabItem["loader"]): TabItem => ({ value, icon, loader, labelKey: `alkokh.smartAccounting.tabs.${value}` });
const tabs: TabItem[] = [
  tab("overview", "tabler-layout-dashboard", () => import("@/components/smart-accounting/tabs/SmartAccountingOverviewTab.vue")),
  tab("cashiers", "tabler-table", () => import("@/components/smart-accounting/tabs/SmartAccountingCashiersTab.vue")),
  tab("moves", "tabler-timeline-event", () => import("@/components/smart-accounting/tabs/SmartAccountingMovesTab.vue")),
  tab("drawer", "tabler-cash-register", () => import("@/components/smart-accounting/tabs/SmartAccountingDrawerTab.vue")),
  tab("payments", "tabler-credit-card", () => import("@/components/smart-accounting/tabs/SmartAccountingPaymentsTab.vue")),
  tab("discounts", "tabler-discount", () => import("@/components/smart-accounting/tabs/SmartAccountingDiscountsTab.vue")),
  tab("expenses", "tabler-receipt-2", () => import("@/components/smart-accounting/tabs/SmartAccountingExpensesTab.vue")),
  tab("time", "tabler-clock-hour-4", () => import("@/components/smart-accounting/tabs/SmartAccountingTimeTab.vue")),
  tab("branches", "tabler-building-store", () => import("@/components/smart-accounting/tabs/SmartAccountingBranchesTab.vue")),
  tab("cashier", "tabler-user-dollar", () => import("@/components/smart-accounting/tabs/SmartAccountingCashierTab.vue")),
  tab("calendar", "tabler-calendar", () => import("@/components/smart-accounting/tabs/SmartAccountingCalendarTab.vue")),
  tab("best", "tabler-trophy", () => import("@/components/smart-accounting/tabs/SmartAccountingBestTab.vue")),
  tab("suggestions", "tabler-bulb", () => import("@/components/smart-accounting/tabs/SmartAccountingSuggestionsTab.vue")),
];

const presetItems = computed(() => ACCOUNTING_PERIOD_PRESETS.map(value => ({ value, title: t(`alkokh.smartAccounting.period.${value}`) })));

// The mascot in the header changes its line every few seconds.
const headerLine = ref(0);
let ticker: ReturnType<typeof setInterval> | null = null;
onMounted(() => { ticker = setInterval(() => { headerLine.value += 1; }, 7000); });
onBeforeUnmount(() => { if (ticker) clearInterval(ticker); });
const busiest = computed(() => ctx.cashierAggregates.value[0]?.cashier_name ?? "");
const mascotLine = computed(() => phrase("header", headerLine.value, {
  name: busiest.value || t("alkokh.smartAccounting.title"),
  amount: formatCompactMoney(ctx.totals.value.sales_total),
}));

// An older server leaves new numbers out; their zeros are not real, so say so.
const missingMetricNames = computed(() => new Intl.ListFormat(locale.value, { type: "conjunction" })
  .format(ctx.report.value.missingMetrics.map(m => t(`alkokh.smartAccounting.counts.${m}`))));

const showTabs = computed(() => ctx.state.value === "ok" && ctx.loaded.value);
const firstLoad = computed(() => !ctx.loaded.value);
</script>

<template>
  <div class="sa-page">
    <VCard
      elevation="0"
      border
      class="sa-header mb-4"
    >
      <div class="sa-head">
        <div class="sa-head__text">
          <h1 class="text-h4 d-flex align-center gap-2 flex-wrap">
            {{ t("alkokh.smartAccounting.title") }}
            <VChip
              v-if="ctx.report.value.source === 'mock'"
              color="warning"
              variant="tonal"
              size="small"
              prepend-icon="tabler-flask"
            >
              {{ t("alkokh.smartAccounting.sampleData") }}
            </VChip>
          </h1>
          <p class="text-body-2 text-medium-emphasis mb-0">
            {{ t("alkokh.smartAccounting.subtitle") }}
          </p>
        </div>
        <div class="sa-head__mascot">
          <CatSpeechBubble
            :text="mascotLine"
            tail="end"
          />
          <CatMascot
            pose="sit"
            color="tabby"
            accessory="glasses"
            :size="92"
          />
        </div>
      </div>

      <div class="sa-filters">
        <AppSelect
          v-model="ctx.preset.value"
          :items="presetItems"
          density="compact"
          :label="t('alkokh.smartAccounting.period.label')"
          class="sa-filters__item"
        />
        <template v-if="ctx.preset.value === 'custom'">
          <AppDateTimePicker
            v-model="ctx.customFrom.value"
            density="compact"
            :label="t('alkokh.smartAccounting.period.from')"
            :config="{ enableTime: false, dateFormat: 'Y-m-d' }"
            class="sa-filters__item"
          />
          <AppDateTimePicker
            v-model="ctx.customTo.value"
            density="compact"
            :label="t('alkokh.smartAccounting.period.to')"
            :config="{ enableTime: false, dateFormat: 'Y-m-d' }"
            class="sa-filters__item"
          />
        </template>
        <AppSelect
          v-model="ctx.branches.value"
          :items="ctx.branchItems.value"
          multiple
          chips
          closable-chips
          clearable
          density="compact"
          :label="t('alkokh.smartAccounting.filters.branches')"
          :placeholder="t('alkokh.smartAccounting.filters.allBranches')"
          class="sa-filters__item"
        />
        <AppSelect
          v-model="ctx.tills.value"
          :items="ctx.tillItems.value"
          multiple
          chips
          closable-chips
          clearable
          density="compact"
          :label="t('alkokh.smartAccounting.filters.tills')"
          :placeholder="t('alkokh.smartAccounting.filters.allTills')"
          class="sa-filters__item"
        />
        <AppSelect
          v-model="ctx.cashiers.value"
          :items="ctx.cashierItems.value"
          multiple
          chips
          closable-chips
          clearable
          density="compact"
          :label="t('alkokh.smartAccounting.filters.cashiers')"
          :placeholder="t('alkokh.smartAccounting.filters.allCashiers')"
          class="sa-filters__item sa-filters__item--wide"
        />
        <div class="sa-filters__actions">
          <VProgressCircular
            v-if="ctx.loading.value"
            indeterminate
            size="20"
            width="2"
            color="primary"
          />
          <VBtn
            icon="tabler-refresh"
            variant="text"
            size="small"
            :aria-label="t('alkokh.smartAccounting.actions.refresh')"
            @click="ctx.load"
          />
          <VBtn
            icon="tabler-settings"
            variant="text"
            size="small"
            :aria-label="t('alkokh.smartAccounting.settings.title')"
            @click="ctx.openSettings()"
          />
          <VMenu
            location="bottom end"
            max-width="420"
          >
            <template #activator="{ props: menu }">
              <VBtn
                v-bind="menu"
                icon="tabler-info-circle"
                variant="text"
                size="small"
                :aria-label="t('alkokh.smartAccounting.definitions.title')"
              />
            </template>
            <VCard>
              <VCardItem>
                <VCardTitle>{{ t("alkokh.smartAccounting.definitions.title") }}</VCardTitle>
                <VCardSubtitle class="text-wrap">
                  {{ t("alkokh.smartAccounting.definitions.subtitle") }}
                </VCardSubtitle>
              </VCardItem>
              <VCardText>
                <div
                  v-for="m in CASHIER_METRICS"
                  :key="m"
                  class="sa-def"
                >
                  <VIcon
                    :icon="METRIC_LOOK[m].icon"
                    :color="METRIC_LOOK[m].color"
                    size="18"
                  />
                  <div>
                    <div class="font-weight-bold text-body-2">
                      {{ t(`alkokh.smartAccounting.counts.${m}`) }}
                    </div>
                    <div class="text-caption text-medium-emphasis">
                      {{ t(`alkokh.smartAccounting.definitions.${m}`) }}
                    </div>
                  </div>
                </div>
                <div class="sa-def">
                  <VIcon
                    icon="tabler-scale"
                    color="secondary"
                    size="18"
                  />
                  <div>
                    <div class="font-weight-bold text-body-2">
                      {{ t("alkokh.smartAccounting.definitions.settlementTitle") }}
                    </div>
                    <div class="text-caption text-medium-emphasis">
                      {{ t("alkokh.smartAccounting.definitions.settlement") }}
                    </div>
                  </div>
                </div>
              </VCardText>
            </VCard>
          </VMenu>
        </div>
      </div>
    </VCard>

    <VAlert
      v-if="showTabs && missingMetricNames"
      type="info"
      variant="tonal"
      density="compact"
      icon="tabler-flask-off"
      class="mb-4"
    >
      {{ t("alkokh.smartAccounting.states.missingMetrics", { metrics: missingMetricNames }) }}
    </VAlert>

    <AppTabs
      v-if="showTabs"
      v-model="activeTab"
      :items="tabs"
      variant="underline"
      sync-mode="query"
      sync-key="tab"
      default-tab="overview"
      card
      tabs-class="px-4"
    />

    <!-- Before the first answer, and when it fails, there are no tabs to show. -->
    <VCard
      v-else
      elevation="0"
      border
    >
      <VCardText class="sa-state">
        <template v-if="firstLoad">
          <CatMascot
            pose="play"
            color="tabby"
            :size="120"
          />
          <span class="text-medium-emphasis">{{ t("alkokh.smartAccounting.states.loading") }}</span>
        </template>
        <template v-else-if="ctx.state.value === 'not_deployed'">
          <CatMascot
            pose="sit"
            mood="grumpy"
            color="grey"
            :size="120"
          />
          <VAlert
            type="info"
            variant="tonal"
            icon="tabler-info-circle"
          >
            {{ t("alkokh.smartAccounting.states.not_deployed") }}
          </VAlert>
        </template>
        <template v-else>
          <CatMascot
            pose="sit"
            mood="grumpy"
            color="black"
            :size="120"
          />
          <VAlert
            type="error"
            variant="tonal"
          >
            {{ t("alkokh.smartAccounting.states.failed") }}
            <template #append>
              <VBtn
                size="small"
                variant="text"
                @click="ctx.load"
              >
                {{ t("alkokh.smartAccounting.actions.retry") }}
              </VBtn>
            </template>
          </VAlert>
        </template>
      </VCardText>
    </VCard>

    <SmartAccountingDrilldownDialog />
    <SmartAccountingSettingsDialog />
  </div>
</template>

<style scoped>
.sa-page :deep(.app-tabs__window) {
  margin-block-start: 16px;
  overflow: visible;
}

.sa-head {
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  padding-block: 20px 4px;
  padding-inline: 20px;
  background:
    radial-gradient(circle at 92% 30%, rgba(var(--v-theme-success), 0.18), transparent 40%),
    radial-gradient(circle at 0% 0%, rgba(var(--v-theme-primary), 0.12), transparent 45%);
}

.sa-head__text {
  flex: 1 1 280px;
  padding-block-end: 12px;
}

.sa-head__mascot {
  display: flex;
  align-items: flex-end;
  gap: 6px;
  margin-block-end: -10px;
}

.sa-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  padding-block: 14px;
  padding-inline: 20px;
  border-block-start: 1px solid rgba(var(--v-theme-on-surface), 0.08);
}

.sa-filters__item {
  flex: 1 1 170px;
  max-inline-size: 240px;
}

.sa-filters__item--wide {
  max-inline-size: 340px;
}

.sa-filters__actions {
  display: flex;
  align-items: center;
  gap: 2px;
  margin-inline-start: auto;
}

.sa-def {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-block-end: 10px;
}

.sa-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding-block: 48px;
}

@media (max-width: 600px) {
  .sa-head__mascot {
    display: none;
  }

  .sa-filters__item,
  .sa-filters__item--wide {
    max-inline-size: none;
  }
}
</style>
